import { getConfig } from "@/features/branding";
import { DownloadLocationsButton, DownloadMetadataButton } from "@/features/collection_home";
import { useMaxAssetCountForCollectionHome } from "@/features/collection_home/utils";
import { PortfolioExportAssetLevelDownloadButton } from "@/features/pe_aldd";
import { RemoteJobActionsProps } from "@/features/remote_jobs";
import { getCollectionToolBinding } from "@/features/reports/types";
import { store, StoreState } from "@/store";
import { supabaseApi, useGetToolTemplateQuery } from "@/store/services/supabase";
import { dispatchWithRetries } from "@/store/services/supabase/utils";
import { portfolioExportActions } from "@/store/slices/portfolioExport";
import { useSummaryActions } from "@/tools/aggregate/portfolio-export/hooks/useSummaryActions";
import { usePortfolioTutorialsState } from "@/tools/aggregate/portfolio-export/PortfolioTutorialContext";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { getCanReprocessForAdaptations, getIsAdaptationProcessing } from "@/tools/aggregate/portfolio-export/utils/progress";
import { createAdaptationsForExistingAssets } from "@/tools/aggregate/portfolio-export/utils/supabase";
import { useCanUseSeverities } from "@/utils/hooks/useCanUseSeverities";
import { useIsOwner } from "@/utils/hooks/useIsOwner";
import { useKeys } from "@/utils/hooks/useKeys";
import { CloudDownloadOutlined, DownloadOutlined, DownOutlined, EyeOutlined, FileAddOutlined, FilePdfOutlined, LoadingOutlined, MoreOutlined, StopOutlined } from "@ant-design/icons";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import { BoxArrowUpIcon } from "@phosphor-icons/react/dist/ssr";
import { Button, ButtonProps, Dropdown, Modal, notification, Tooltip } from "antd";
import { MenuProps } from "antd/lib";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getAnalysisCollectionDeleteConfirm } from "../../common/AnalysisCollectionDeleteConfirm";
import { AnalysisCollectionEditModal, type AnalysisCollectionEditValues } from "../../common/AnalysisCollectionEditModal";
import { SummaryDownloadModal } from "../hooks/SummaryDownloadModal";
import { UpdateAvailableModal } from "./UpdateAvailableModal";

// Client only: the report data loaders it pulls in import Leaflet, which needs `window` at import time.
const CollectionReportModal = dynamic(() => import("@/features/pe_reportdd/components/CollectionReportModal"), {
  ssr: false
});

/**
 *
 */
interface ActionButtonProps extends ButtonProps {
  isRaw?: boolean;
  itemKey?: string;
}
export type PortfolioActionRefs = {
  actions: MutableRefObject<null>;
};
export type PortfolioExportJobActionsProps = Omit<RemoteJobActionsProps<PortfolioExportJobSchema>, "status" | "completedActions"> & {
  canRunAutoAdaptations: boolean;
};
export const PortfolioExportJobActions = (props: PortfolioExportJobActionsProps) => {
  const {
    t
  } = useTranslation("terms");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showUpdateAvailableModal, setShowUpdateAvailableModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const isRerunning = useSelector((state: StoreState) => state.portfolioExport.rerunningPortfolioIds.includes(props.schema.id));
  const pendingRerunId = useSelector((state: StoreState) => state.portfolioExport.pendingRerunPortfolioId);
  const autoStartFired = useRef(false);
  const [modal, modalContext] = Modal.useModal();
  const router = useRouter();
  const [key] = useKeys();
  const isOwner = useIsOwner();
  const maxAssetCount = useMaxAssetCountForCollectionHome();
  const summary = useSummaryActions();
  const canUseSeverities = useCanUseSeverities();
  // Reports are offered once the tool has a live template.
  const {
    data: reportTemplateId
  } = useGetToolTemplateQuery(getCollectionToolBinding(props.schema.isLargeSite));
  const isSummaryProcessing = !!props.schema.summaryId && !props.schema.isSummaryGenerated;
  const isSummaryGenerated = !!props.schema.summaryId && props.schema.isSummaryGenerated;
  const buttonsDisabled = !props.schema.assets.processedCount;
  const isAdaptationProcessing = getIsAdaptationProcessing(props.schema, props.canRunAutoAdaptations);

  // Named so the auto-start effect and the button's JSX can't drift apart.
  const canShowUpdateAvailable = getCanReprocessForAdaptations(props.schema, props.canRunAutoAdaptations);
  const downloadDropdownMenuItems: MenuProps["items"] = [];
  const buttons: (ActionButtonProps | undefined)[] = [];
  const {
    portfolioItem,
    isTutorialOpen,
    showDownloadDropdown,
    showMoreDropdown,
    tutorialPortfolioSchema,
    selectedDownloadItemIndex,
    showPointLevelResultsModal,
    outputTypeModal,
    editModal,
    downloadsMenuPopup
  } = usePortfolioTutorialsState();
  const isTutorialPortfolio = isTutorialOpen && tutorialPortfolioSchema.id === props.schema.id;
  const onDeleteJob = async (name: string, action: "delete" | "cancel" = "delete") => {
    if (isTutorialOpen) return;
    const confirmed = await modal.confirm(getAnalysisCollectionDeleteConfirm(name, action));
    if (confirmed) {
      props.onDeleteJob();
    }
  };
  const handleSummaryOpen = () => {
    store.dispatch(portfolioExportActions.setSummaryStatusModal({
      isOpen: true,
      highlightedSummaryId: props.schema.summaryId
    }));
  };
  switch (props.schema.status) {
    case "completed_with_errors":
    case "completed":
      if (props.canRunAutoAdaptations && props.schema.autoAdaptationStatus !== null) {
        downloadDropdownMenuItems.push({
          key: "summary",
          label: <span className="flex items-center gap-2">
              <CloudDownloadOutlined /> Aggregated Summary Results
            </span>,
          disabled: buttonsDisabled,
          onClick: () => {
            if (isTutorialOpen) return;
            setShowSummaryModal(true);
          }
        });
      } else if (isSummaryProcessing) {
        downloadDropdownMenuItems.push({
          key: "summary_status",
          label: <span className="flex items-center gap-2">
              <LoadingOutlined spin /> Generating Aggregated Summary Results...
            </span>,
          onClick: () => {
            if (isTutorialOpen) return;
            handleSummaryOpen();
          }
        });
      } else if (isSummaryGenerated) {
        downloadDropdownMenuItems.push({
          key: "summary_download",
          label: <span className="flex items-center gap-2">
              <DownloadOutlined /> Aggregated Summary Results
            </span>,
          disabled: summary.isDownloading || buttonsDisabled,
          onClick: async () => {
            if (isTutorialOpen) return;
            await summary.download(props.schema);
          }
        });
      } else {
        downloadDropdownMenuItems.push({
          key: "summary_generate",
          label: <span className="flex items-center gap-2">
              <FileAddOutlined /> Generate Aggregated Summary Results
            </span>,
          disabled: summary.isGenerating || buttonsDisabled,
          onClick: async () => {
            if (isTutorialOpen) return;
            await summary.generate(props.schema);
          }
        });
      }
      downloadDropdownMenuItems.push({
        key: "asset_level",
        label: <PortfolioExportAssetLevelDownloadButton schema={props.schema} disabled={buttonsDisabled} textWithIcon isTutorialOpen={isTutorialOpen} showPointLevelResultsModal={showPointLevelResultsModal} outputTypeModalRef={outputTypeModal} />,
        disabled: buttonsDisabled
      });
      downloadDropdownMenuItems.push({
        key: "metadata",
        label: <span className="flex items-center gap-2">
            <DownloadMetadataButton schema={props.schema} textWithIcon />
          </span>,
        disabled: buttonsDisabled
      });
      if (reportTemplateId) {
        downloadDropdownMenuItems.push({
          key: "report",
          label: <span className="flex items-center gap-2">
              <FilePdfOutlined /> Report
            </span>,
          disabled: buttonsDisabled,
          onClick: () => {
            if (isTutorialOpen) return;
            setShowReportModal(true);
          }
        });
      }
      if (canUseSeverities) {
        downloadDropdownMenuItems.push({
          key: "locations",
          label: <DownloadLocationsButton schema={props.schema} disabled={buttonsDisabled} textWithIcon isTutorialOpen={isTutorialOpen} />,
          disabled: buttonsDisabled
        });
      }
      if (getConfig().pages.collections.enabled) {
        buttons.push({
          itemKey: "view_results",
          icon: <EyeOutlined />,
          children: "View Results",
          disabled: props.schema.assets.totalCount > maxAssetCount || buttonsDisabled,
          className: "!font-inherit",
          onClick: () => {
            router.push((props.schema.isLargeSite ? "/large-sites/" : "/portfolios/") + props.schema.id);
          },
          title: props.schema.assets.totalCount > maxAssetCount ? `Can only view up to ${maxAssetCount} ${props.schema.isLargeSite ? "points" : "assets"} on map` : undefined
        });
      }
      break;
    case "error":
      buttons.push({
        icon: <DeleteOutlined />,
        children: "Delete",
        onClick: async () => await onDeleteJob(props.schema.name)
      });
      break;
    default:
      buttons.push({
        icon: <StopOutlined />,
        children: "Cancel",
        onClick: async () => await onDeleteJob(props.schema.name, "cancel")
      });
  }
  const threeDotMenuItems: MenuProps["items"] = [{
    key: "edit-portfolio",
    label: "Edit...",
    onClick: async () => {
      if (isTutorialOpen) return;
      setShowEditModal(true);
    }
  }, {
    key: "delete-portfolio",
    label: "Delete",
    onClick: async () => await onDeleteJob(props.schema.name),
    icon: <DeleteOutlined />
  }];
  const handleEditOk = async (val: AnalysisCollectionEditValues<PortfolioExportJobSchema>) => {
    if (props.onEditJob) {
      await props.onEditJob(val);
    }
  };
  const [selectedDownloadKeys, setSelectedDownloadKeys] = useState<string>("");
  useEffect(() => {
    if (!isTutorialOpen || selectedDownloadItemIndex === undefined) {
      setSelectedDownloadKeys("");
      return;
    }
    const defaultActiveKey = downloadDropdownMenuItems.length > selectedDownloadItemIndex ? downloadDropdownMenuItems[selectedDownloadItemIndex]?.key : undefined;
    if (downloadDropdownMenuItems.length > selectedDownloadItemIndex && defaultActiveKey !== undefined) {
      setSelectedDownloadKeys(String(defaultActiveKey));
    } else setSelectedDownloadKeys("");
  }, [selectedDownloadItemIndex]);
  const runRerunWithAdaptations = async () => {
    store.dispatch(portfolioExportActions.addRerunningPortfolio(props.schema.id));
    const assetsAction = supabaseApi.endpoints.getAllPortfolioAssetsEncrypted.initiate(props.schema.id);
    type AssetsResult = (typeof supabaseApi.endpoints.getAllPortfolioAssetsEncrypted)["Types"]["ResultType"];
    let encryptedAssets: AssetsResult;
    try {
      encryptedAssets = await dispatchWithRetries<AssetsResult>({
        action: assetsAction,
        maxRetries: 10,
        timeout: 5000
      });
    } catch (error) {
      console.log("Error fetching portfolio assets", error);
      notification.error({
        message: "Could not start re-processing",
        description: "Failed to load the analysis data. Please try again."
      });
      store.dispatch(portfolioExportActions.removeRerunningPortfolio(props.schema.id));
      return;
    }
    const portfolioAssets = (encryptedAssets ?? []).map(({
      id,
      data,
      encryptedId
    }) => ({
      id,
      data,
      encrypted_id: encryptedId
    }));
    try {
      const result = await createAdaptationsForExistingAssets({
        secret: props.schema.secret,
        portfolioId: props.schema.id,
        portfolioAssets
      });
      if (result.type !== "success") {
        notification.error({
          message: "Could not start re-processing",
          description: result.errors.join(" ")
        });
        store.dispatch(portfolioExportActions.removeRerunningPortfolio(props.schema.id));
        return;
      }
      store.dispatch(supabaseApi.util.invalidateTags([{
        type: "Portfolio",
        id: "List"
      }]));
      try {
        await store.dispatch(supabaseApi.endpoints.updatePortfolioRerun.initiate({
          id: props.schema.id
        })).unwrap();
      } catch (error) {
        console.log("Error updating portfolio rerun details", error);
      }
    } catch (error) {
      console.log("Error creating adaptation for existing asset", error);
      notification.error({
        message: "Could not start re-processing",
        description: "Something went wrong while re-processing this analysis. Please try again."
      });
      store.dispatch(portfolioExportActions.removeRerunningPortfolio(props.schema.id));
    }
  };

  /**
   * Runs a re-process the user confirmed on the collection page - the compute
   * lives here, so the intent travels to the row. Not guarded on `isRerunning`:
   * the collection page sets that before navigating, which would block every
   * auto-start. The ref and the synchronous claim below keep it to one run.
   */
  useEffect(() => {
    if (autoStartFired.current) return;
    if (pendingRerunId !== props.schema.id) return;
    if (!canShowUpdateAvailable || isTutorialOpen) return;
    autoStartFired.current = true;
    store.dispatch(portfolioExportActions.clearPendingRerun());
    runRerunWithAdaptations();
  }, [pendingRerunId, props.schema.id, canShowUpdateAvailable, isTutorialOpen]);
  const handleCancelAdaptations = async () => {
    if (isTutorialOpen) return;
    const confirmed = await modal.confirm({
      title: `Cancel ${t("automatedResilience")} Runs`,
      content: `This will stop the ${t("automatedResilience")} run and remove all ${t("automatedResilience")} results for this portfolio. Do you want to continue?`,
      okText: "Cancel run",
      okButtonProps: {
        danger: true
      },
      cancelText: "Back"
    });
    if (!confirmed) return;
    setIsCancelling(true);
    try {
      await store.dispatch(supabaseApi.endpoints.deletePortfolioAdaptations.initiate(props.schema.id)).unwrap();
      store.dispatch(portfolioExportActions.removeRerunningPortfolio(props.schema.id));
    } catch (error) {
      console.log("Error cancelling automated resilience run", error);
    } finally {
      setIsCancelling(false);
    }
  };
  return <div style={{
    display: "flex",
    gap: "0.25rem",
    alignItems: "center",
    fontWeight: 400
  }} data-sentry-component="PortfolioExportJobActions" data-sentry-source-file="PortfolioExportJobActions.tsx">
      {modalContext}

      <div ref={isTutorialOpen && props.schema.id === tutorialPortfolioSchema.id ? portfolioItem.downloadsMenu : undefined}>
        {canShowUpdateAvailable && <Button onClick={() => {
        if (isTutorialOpen) return;
        setShowUpdateAvailableModal(true);
      }} variant="outlined" className="mr-1" icon={<BoxArrowUpIcon weight="bold" />} loading={isRerunning} color="primary">
            Update available
          </Button>}

        {isAdaptationProcessing && <Tooltip title={`Cancels ${t("automatedResilience")} run and removes ${t("automatedResilience")} results`}>
            <Button onClick={handleCancelAdaptations} icon={<StopOutlined />} loading={isCancelling}>
              Cancel
            </Button>
          </Tooltip>}

        {!isAdaptationProcessing && ["completed", "completed_with_errors"].includes(props.schema.status) && <Dropdown ref={isTutorialPortfolio ? downloadsMenuPopup : undefined} trigger={["click"]} menu={{
        items: downloadDropdownMenuItems,
        activeKey: selectedDownloadKeys,
        onClick: e => {
          if (isTutorialOpen) {
            e.domEvent.stopPropagation();
            e.domEvent.preventDefault();
          }
        }
      }} open={!isTutorialOpen ? undefined : showDownloadDropdown && tutorialPortfolioSchema.id === props.schema.id}>
            <Button icon={<DownOutlined />} iconPlacement="end" loading={summary.isGenerating || summary.isDownloading}>
              <span className="flex items-center gap-2">
                <CloudDownloadOutlined /> Download
              </span>
            </Button>
          </Dropdown>}
      </div>

      {!isAdaptationProcessing && buttons.map((button, index) => {
      if (!button) {
        return undefined;
      }
      if (button.isRaw) {
        return <React.Fragment key={index}>{button.children}</React.Fragment>;
      }
      return <div key={index} ref={isTutorialOpen && props.schema.id === tutorialPortfolioSchema.id && "itemKey" in button && button["itemKey"] === "view_results" ? portfolioItem.viewResultsTrigger : undefined}>
              <Button key={index} style={{
          fontWeight: "inherit"
        }} {...button} />
            </div>;
    })}

      <div ref={isTutorialOpen && props.schema.id === tutorialPortfolioSchema.id ? portfolioItem.actionsMenu : undefined}>
        <Dropdown trigger={["click"]} disabled={isAdaptationProcessing || !["completed", "completed_with_errors"].includes(props.schema.status)} menu={{
        items: threeDotMenuItems
      }} styles={{
        root: {
          width: 160
        }
      }} open={!isTutorialOpen ? undefined : showMoreDropdown && tutorialPortfolioSchema.id === props.schema.id} data-sentry-element="Dropdown" data-sentry-source-file="PortfolioExportJobActions.tsx">
          <Button icon={<MoreOutlined />} style={{
          fontWeight: "inherit"
        }} type="text" data-sentry-element="Button" data-sentry-source-file="PortfolioExportJobActions.tsx" />
        </Dropdown>
      </div>
      <UpdateAvailableModal open={showUpdateAvailableModal} portfolioType={props.schema.regionType} onConfirm={() => {
      setShowUpdateAvailableModal(false);
      runRerunWithAdaptations();
    }} onCancel={() => setShowUpdateAvailableModal(false)} data-sentry-element="UpdateAvailableModal" data-sentry-source-file="PortfolioExportJobActions.tsx" />
      <SummaryDownloadModal schema={props.schema} summary={summary} open={showSummaryModal} onClose={() => setShowSummaryModal(false)} data-sentry-element="SummaryDownloadModal" data-sentry-source-file="PortfolioExportJobActions.tsx" />
      {reportTemplateId && showReportModal && <CollectionReportModal schema={props.schema} templateId={reportTemplateId} open onClose={() => setShowReportModal(false)} />}
      {props.onEditJob && <AnalysisCollectionEditModal open={showEditModal} isOwner={isOwner || false} schema={props.schema} onCancel={() => setShowEditModal(false)} onOk={handleEditOk} hasOrgKeys={!!key.orgSecret} editModalRef={editModal} />}
    </div>;
};